// src/pages/negocio/VecinosFiadoNegocio.js
//
// Lista de vecinos que tienen fiado con este negocio, agrupados por
// veci. Permite abrir el detalle y registrar abonos desde ahí.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, CardActionArea, Chip, Alert,
  CircularProgress, Divider, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment, MenuItem, LinearProgress,
} from '@mui/material';
import SearchIcon    from '@mui/icons-material/Search';
import PaymentIcon   from '@mui/icons-material/Payment';
import CancelIcon    from '@mui/icons-material/Cancel';

import { fiados as apiFiados } from '../../api/fiadosApi';
import { formatoCOP } from '../../utils/format';

export default function VecinosFiadoNegocio() {
  const { veciId } = useParams();
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');
  const [texto, setTexto]       = useState('');

  const [abonando, setAbonando] = useState(null); // fiado + form
  const [anulando, setAnulando] = useState(null); // fiado + motivo

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await apiFiados.listar();
      setItems(Array.isArray(r) ? r : (r.items || []));
    } catch (e) { setError(e.mensajeUsuario || 'No se pudieron cargar los fiados.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar por veci
  const agrupados = useMemo(() => {
    const map = new Map();
    for (const f of items) {
      const cid = f.clienteId || f.ClienteId;
      if (!map.has(cid)) {
        map.set(cid, {
          clienteId: cid,
          nombre: f.clienteNombre || f.ClienteNombre,
          totalPendiente: 0,
          cantidadFiados: 0,
          fiados: [],
        });
      }
      const g = map.get(cid);
      g.totalPendiente += (f.saldoPendiente || f.SaldoPendiente || 0);
      g.cantidadFiados += 1;
      g.fiados.push(f);
    }
    return Array.from(map.values())
      .sort((a, b) => b.totalPendiente - a.totalPendiente);
  }, [items]);

  const filtrados = useMemo(() => {
    if (!texto.trim()) return agrupados;
    const q = texto.toLowerCase();
    return agrupados.filter(g => g.nombre.toLowerCase().includes(q));
  }, [agrupados, texto]);

  const vecinoAbierto = veciId
    ? agrupados.find(g => String(g.clienteId) === String(veciId))
    : null;

  const registrarAbono = async () => {
    try {
      await apiFiados.registrarAbono(abonando.fiadoId, {
        monto: parseFloat(abonando.monto),
        medio: abonando.medio || null,
        nota:  abonando.nota  || null,
      });
      setAbonando(null); setOk('Abono registrado.');
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo registrar el abono.'); }
  };

  const anular = async () => {
    try {
      await apiFiados.anular(anulando.fiadoId, anulando.motivo || '');
      setAnulando(null); setOk('Fiado anulado.');
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo anular.'); }
  };

  if (cargando) return <CircularProgress />;

  // Vista de un veci específico
  if (veciId) {
    if (!vecinoAbierto) {
      return (
        <Stack spacing={2}>
          <Button onClick={() => navigate('/negocio/vecinos')}>Volver</Button>
          <Alert severity="info">Este veci no tiene fiados con tu negocio.</Alert>
        </Stack>
      );
    }
    return (
      <Stack spacing={3}>
        <Box>
          <Button onClick={() => navigate('/negocio/vecinos')}>← Volver a la lista</Button>
        </Box>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {ok && <Alert severity="success" onClose={() => setOk('')}>{ok}</Alert>}

        <Box>
          <Typography variant="overline" color="text.secondary">Vecino</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>{vecinoAbierto.nombre}</Typography>
          <Typography variant="h5" color="warning.main" sx={{ fontFamily: 'Fraunces' }}>
            Debe {formatoCOP(vecinoAbierto.totalPendiente)}
          </Typography>
        </Box>

        <Stack spacing={2}>
          {vecinoAbierto.fiados.map((f) => <TarjetaFiado
            key={f.id || f.Id} f={f}
            onAbonar={() => setAbonando({ fiadoId: f.id || f.Id, monto: '', medio: 'efectivo', nota: '' })}
            onAnular={() => setAnulando({ fiadoId: f.id || f.Id, motivo: '' })}
          />)}
        </Stack>

        <DialogsAbono {...{ abonando, setAbonando, registrarAbono, anulando, setAnulando, anular }} />
      </Stack>
    );
  }

  // Vista general: lista de vecinos con fiado
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Vecinos con fiado</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Fiados vigentes</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk('')}>{ok}</Alert>}

      <TextField
        fullWidth
        placeholder="Buscar veci por nombre…"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
        }}
      />

      {filtrados.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">Aún no hay vecinos con fiados.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filtrados.map((g) => (
            <Card key={g.clienteId}>
              <CardActionArea onClick={() => navigate(`/negocio/vecinos/${g.clienteId}`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{g.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {g.cantidadFiados} fiado(s)
                      </Typography>
                    </Box>
                    <Chip
                      label={formatoCOP(g.totalPendiente)}
                      color="warning"
                      sx={{ fontWeight: 600, fontSize: 16 }}
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function TarjetaFiado({ f, onAbonar, onAnular }) {
  const original = f.valorOriginal ?? f.ValorOriginal;
  const abonado  = f.totalAbonado ?? f.TotalAbonado ?? 0;
  const saldo    = f.saldoPendiente ?? f.SaldoPendiente ?? 0;
  const estado   = f.estado || f.Estado;
  const conf     = f.estadoConfirmacion || f.EstadoConfirmacion;
  const desc     = f.descripcion || f.Descripcion;
  const progreso = original > 0 ? (abonado / original) * 100 : 0;
  const puedeAbonar = estado === 'VIGENTE';

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            {desc && <Typography variant="body2">{desc}</Typography>}
            <Typography variant="caption" color="text.secondary">
              Emitido {new Date(f.fechaEmision || f.FechaEmision).toLocaleDateString('es-CO')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={estado} size="small" color={estado === 'VIGENTE' ? 'warning' : estado === 'PAGADO' ? 'success' : 'default'} />
            {conf === 'pendiente_confirmacion' && <Chip label="Sin confirmar" size="small" color="info" />}
            {conf === 'reclamado' && <Chip label="Reclamado" size="small" color="error" />}
          </Stack>
        </Box>

        <LinearProgress variant="determinate" value={progreso} sx={{ my: 1, height: 8, borderRadius: 4 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Abonado {formatoCOP(abonado)} de {formatoCOP(original)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Debe {formatoCOP(saldo)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          {puedeAbonar && (
            <Button size="small" variant="contained" startIcon={<PaymentIcon />} onClick={onAbonar}>
              Registrar abono
            </Button>
          )}
          <Button size="small" color="error" startIcon={<CancelIcon />} onClick={onAnular}>
            Anular
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DialogsAbono({ abonando, setAbonando, registrarAbono, anulando, setAnulando, anular }) {
  return (
    <>
      <Dialog open={!!abonando} onClose={() => setAbonando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Registrar abono</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth type="number" label="Monto (COP)"
              value={abonando?.monto || ''}
              onChange={(e) => setAbonando(s => ({ ...s, monto: e.target.value }))}
              autoFocus
            />
            <TextField
              select fullWidth label="Medio de pago"
              value={abonando?.medio || 'efectivo'}
              onChange={(e) => setAbonando(s => ({ ...s, medio: e.target.value }))}
            >
              {['efectivo','transferencia','nequi','daviplata','otro'].map(m => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth multiline label="Nota (opcional)" minRows={2}
              value={abonando?.nota || ''}
              onChange={(e) => setAbonando(s => ({ ...s, nota: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbonando(null)}>Cancelar</Button>
          <Button variant="contained" onClick={registrarAbono} disabled={!abonando?.monto}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!anulando} onClose={() => setAnulando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Anular fiado</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline label="Motivo" minRows={2}
            value={anulando?.motivo || ''}
            onChange={(e) => setAnulando(s => ({ ...s, motivo: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnulando(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={anular}>Anular</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
